import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { UploadSimple, Trash as TrashIcon, Image as ImageIcon } from "@phosphor-icons/react";
import { api } from "@/lib/api";

/**
 * Admin image-upload widget.
 *
 * Upload-only workflow (no URL paste): admins pick a file, we POST it to
 * /admin/images/upload with `onUploadProgress` so a live percentage bar
 * animates over the widget. While uploading:
 *   - the Upload button is disabled
 *   - a determinate progress bar appears
 *   - the parent gets notified via `onUploadStateChange(true)` so the parent
 *     form can gate its Save button until the upload settles
 *
 * On success the returned absolute URL replaces `value` and a preview
 * thumbnail is rendered. Clicking the trash icon clears the field.
 */
const ImageUploadField = ({ value, onChange, onUploadStateChange, testid, endpoint = "/admin/images/upload" }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  // After a successful upload we keep the before/after bytes so admins can see
  // exactly how much bandwidth the compressor saved. Cleared when the field
  // is reset or the image is replaced.
  const [sizes, setSizes] = useState(null); // { original, optimized }

  const setBusy = (b) => {
    setUploading(b);
    onUploadStateChange?.(b);
  };

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) {
      toast.error("Image too large. Max 8 MB.");
      return;
    }
    setBusy(true);
    setProgress(0);
    setSizes(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post(endpoint, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (!evt.total) return;
          setProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      });
      onChange(r.data?.url || "");
      setProgress(100);
      // Backend returns { size, original_size } — surface both so the admin
      // sees the "5.4 MB → 731 KB" compression win right in the widget.
      const optimized = r.data?.size ?? null;
      const original = r.data?.original_size ?? f.size;
      if (optimized != null) setSizes({ original, optimized });
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      // Brief hold so users see the completed bar before it disappears.
      setTimeout(() => setProgress(0), 400);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const clearField = () => {
    onChange("");
    setSizes(null);
  };

  return (
    <div className="md-image-upload" data-testid={`image-upload-${testid || "img"}`}>
      <div className="md-image-upload-row">
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="btn-outline text-sm md-image-upload-btn md-image-upload-btn-primary"
          disabled={uploading}
          data-testid={`image-upload-btn-${testid || "img"}`}
        >
          <UploadSimple size={14} weight="bold" />
          {uploading ? `Uploading ${progress}%` : value ? "Replace image" : "Upload image"}
        </button>
        {value && !uploading && (
          <button
            type="button"
            onClick={clearField}
            className="btn-outline text-sm md-image-upload-clear"
            aria-label="Remove image"
            data-testid={`image-clear-${testid || "img"}`}
          >
            <TrashIcon size={14} weight="bold" />
          </button>
        )}
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          onChange={onPick}
          style={{ display: "none" }}
          data-testid={`image-input-${testid || "img"}`}
        />
      </div>
      {uploading && (
        <div className="md-image-upload-progress" data-testid={`image-progress-${testid || "img"}`}>
          <div className="md-image-upload-progress-bar" style={{ width: `${progress}%` }} />
        </div>
      )}
      {value && !uploading && (
        <div className="md-image-upload-preview" data-testid={`image-preview-${testid || "img"}`}>
          <ImageIcon size={12} weight="duotone" />
          <img
            src={value}
            alt="Preview"
            onError={(e) => { e.target.style.display = "none"; }}
          />
        </div>
      )}
      {sizes && !uploading && <SizeChip original={sizes.original} optimized={sizes.optimized} testid={testid} />}
    </div>
  );
};

const formatBytes = (n) => {
  if (n == null) return "";
  if (n >= 1024 * 1024) return `${(n / 1024 / 1024).toFixed(2)} MB`;
  if (n >= 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${n} B`;
};

const SizeChip = ({ original, optimized, testid }) => {
  const savedPct = original > 0 ? Math.max(0, Math.round((1 - optimized / original) * 100)) : 0;
  return (
    <div className="md-image-upload-size" data-testid={`image-size-${testid || "img"}`}>
      <span className="md-image-upload-size-before">{formatBytes(original)}</span>
      <span className="md-image-upload-size-arrow" aria-hidden>→</span>
      <span className="md-image-upload-size-after">{formatBytes(optimized)}</span>
      {savedPct > 0 && (
        <span className="md-image-upload-size-saved" data-testid={`image-size-saved-${testid || "img"}`}>
          {savedPct}% smaller
        </span>
      )}
    </div>
  );
};

export default ImageUploadField;
